import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAlcoholType } from "./enums/enumAlcoholType.generated";
import { EnumBillingPlanStatus } from "./enums/enumBillingPlanStatus.generated";

// Model Objects

export class BillingSupportSummary extends ModelObject<BillingSupportSummary> {

    public tenantGroupId: Guid = Guid.createEmpty();
    public planStatus: EnumBillingPlanStatus = EnumBillingPlanStatus.trialling;
    public permittedConcurrentUsers: number = 0;
    private _freeTrialExpirationDate: LocalDate | null = null;
    public get freeTrialExpirationDate(): LocalDate | null { return this._freeTrialExpirationDate }
    public set freeTrialExpirationDate(value: LocalDate | null) {
      this._freeTrialExpirationDate = value === undefined ? null : value;
    }
    public stripeSubscriptionId: string = "";
    public stripeCustomerId: string = "";
    private _cancelledAt: Instant | null = null;
    public get cancelledAt(): Instant | null { return this._cancelledAt }
    public set cancelledAt(value: Instant | null) {
      this._cancelledAt = value === undefined ? null : value;
    }
    public hasPaymentMethod: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
          this.planStatus = EnumBillingPlanStatus.getByValue(jsonSerializedObject.planStatus, this.planStatus);
          this.permittedConcurrentUsers = jsonSerializedObject.permittedConcurrentUsers;
          this.freeTrialExpirationDate = jsonSerializedObject.freeTrialExpirationDate == null ? null : LocalDate.parse(jsonSerializedObject.freeTrialExpirationDate);
          this.stripeSubscriptionId = jsonSerializedObject.stripeSubscriptionId;
          this.stripeCustomerId = jsonSerializedObject.stripeCustomerId;
          this.cancelledAt = jsonSerializedObject.cancelledAt == null ? null : Instant.parse(jsonSerializedObject.cancelledAt);
          this.hasPaymentMethod = jsonSerializedObject.hasPaymentMethod;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantGroupId = this.tenantGroupId.toString();
        toRet.planStatus = this.planStatus.value;
        toRet.permittedConcurrentUsers = this.permittedConcurrentUsers;
        toRet.freeTrialExpirationDate = this.freeTrialExpirationDate == null ? null : this.freeTrialExpirationDate.toString();
        toRet.stripeSubscriptionId = this.stripeSubscriptionId;
        toRet.stripeCustomerId = this.stripeCustomerId;
        toRet.cancelledAt = this.cancelledAt == null ? null : this.cancelledAt.toString();
        toRet.hasPaymentMethod = this.hasPaymentMethod;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "BillingSupportSummary.TenantGroupId cannot be empty"},
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v >= -2147483648 || "PermittedConcurrentUsers is not greater than minimum allowed value: -2147483648"},
            {field: "permittedConcurrentUsers", rule: (v: any) => v == null || v <= 2147483647 || "PermittedConcurrentUsers is above maximum allowed value: 2147483647"},
    ];
}


export class TenantGroupSupportSummary extends ModelObject<TenantGroupSupportSummary> {

    public tenantGroupId: Guid = Guid.createEmpty();
    public tenantGroupName: string = "";
    public tenantGroupCreated: Instant = Instant.ofEpochMilli(0);
    public allowEmails: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantGroupId = Guid.parseOrEmpty(jsonSerializedObject.tenantGroupId);
          this.tenantGroupName = jsonSerializedObject.tenantGroupName;
          this.tenantGroupCreated = Instant.parse(jsonSerializedObject.tenantGroupCreated);
          this.allowEmails = jsonSerializedObject.allowEmails;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantGroupId = this.tenantGroupId.toString();
        toRet.tenantGroupName = this.tenantGroupName;
        toRet.tenantGroupCreated = this.tenantGroupCreated.toString();
        toRet.allowEmails = this.allowEmails;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantGroupId", rule: (v: any) => !v.isEmpty() || "TenantGroupSupportSummary.TenantGroupId cannot be empty"},
    ];
}


export class ContinuableLog extends ModelObject<ContinuableLog> {

    public log: string = "";
    private _continuation: Guid | null = null;
    public get continuation(): Guid | null { return this._continuation }
    public set continuation(value: Guid | null) {
      this._continuation = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.log = jsonSerializedObject.log;
          this.continuation = jsonSerializedObject.continuation == null ? null : Guid.parseOrEmpty(jsonSerializedObject.continuation);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.log = this.log;
        toRet.continuation = this.continuation == null ? null : this.continuation.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "continuation", rule: (v: any) => v == null || !v.isEmpty() || "ContinuableLog.Continuation cannot be empty"},
    ];
}


export class ContinuableLogString extends ModelObject<ContinuableLogString> {

    public log: string = "";
    public continuation: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.log = jsonSerializedObject.log;
          this.continuation = jsonSerializedObject.continuation;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.log = this.log;
        toRet.continuation = this.continuation;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DutyUpdateLine extends ModelObject<DutyUpdateLine> {

    public tenantId: Guid = Guid.createEmpty();
    public lineId: Guid = Guid.createEmpty();
    public orderOrCreditNumber: string = "";
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    public productCode: string = "";
    public quantity: number = 0;
    public currentAlcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public newAlcoholType: EnumAlcoholType = EnumAlcoholType.none;
    public currentAbv: number = 0;
    public newAbv: number = 0;
    public currentUnitDutiableLitres: number = 0;
    public newUnitDutiableLitres: number = 0;
    private _currentManufacturerId: Guid | null = null;
    public get currentManufacturerId(): Guid | null { return this._currentManufacturerId }
    public set currentManufacturerId(value: Guid | null) {
      this._currentManufacturerId = value === undefined ? null : value;
    }
    private _newManufacturerId: Guid | null = null;
    public get newManufacturerId(): Guid | null { return this._newManufacturerId }
    public set newManufacturerId(value: Guid | null) {
      this._newManufacturerId = value === undefined ? null : value;
    }
    public currentManufacturerName: string = "";
    public newManufacturerName: string = "";
    public currentIsDesignedForPump: boolean = false;
    public newIsDesignedForPump: boolean = false;
    private _currentFinalUnitLitres: number | null = null;
    public get currentFinalUnitLitres(): number | null { return this._currentFinalUnitLitres }
    public set currentFinalUnitLitres(value: number | null) {
      this._currentFinalUnitLitres = value === undefined ? null : value;
    }
    private _newFinalUnitLitres: number | null = null;
    public get newFinalUnitLitres(): number | null { return this._newFinalUnitLitres }
    public set newFinalUnitLitres(value: number | null) {
      this._newFinalUnitLitres = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.lineId = Guid.parseOrEmpty(jsonSerializedObject.lineId);
          this.orderOrCreditNumber = jsonSerializedObject.orderOrCreditNumber;
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.productCode = jsonSerializedObject.productCode;
          this.quantity = jsonSerializedObject.quantity;
          this.currentAlcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.currentAlcoholType, this.currentAlcoholType);
          this.newAlcoholType = EnumAlcoholType.getByValue(jsonSerializedObject.newAlcoholType, this.newAlcoholType);
          this.currentAbv = jsonSerializedObject.currentAbv;
          this.newAbv = jsonSerializedObject.newAbv;
          this.currentUnitDutiableLitres = jsonSerializedObject.currentUnitDutiableLitres;
          this.newUnitDutiableLitres = jsonSerializedObject.newUnitDutiableLitres;
          this.currentManufacturerId = jsonSerializedObject.currentManufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.currentManufacturerId);
          this.newManufacturerId = jsonSerializedObject.newManufacturerId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.newManufacturerId);
          this.currentManufacturerName = jsonSerializedObject.currentManufacturerName;
          this.newManufacturerName = jsonSerializedObject.newManufacturerName;
          this.currentIsDesignedForPump = jsonSerializedObject.currentIsDesignedForPump;
          this.newIsDesignedForPump = jsonSerializedObject.newIsDesignedForPump;
          this.currentFinalUnitLitres = jsonSerializedObject.currentFinalUnitLitres == null ? null : jsonSerializedObject.currentFinalUnitLitres;
          this.newFinalUnitLitres = jsonSerializedObject.newFinalUnitLitres == null ? null : jsonSerializedObject.newFinalUnitLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.lineId = this.lineId.toString();
        toRet.orderOrCreditNumber = this.orderOrCreditNumber;
        toRet.despatchDate = this.despatchDate.toString();
        toRet.productCode = this.productCode;
        toRet.quantity = this.quantity;
        toRet.currentAlcoholType = this.currentAlcoholType.value;
        toRet.newAlcoholType = this.newAlcoholType.value;
        toRet.currentAbv = this.currentAbv;
        toRet.newAbv = this.newAbv;
        toRet.currentUnitDutiableLitres = this.currentUnitDutiableLitres;
        toRet.newUnitDutiableLitres = this.newUnitDutiableLitres;
        toRet.currentManufacturerId = this.currentManufacturerId == null ? null : this.currentManufacturerId.toString();
        toRet.newManufacturerId = this.newManufacturerId == null ? null : this.newManufacturerId.toString();
        toRet.currentManufacturerName = this.currentManufacturerName;
        toRet.newManufacturerName = this.newManufacturerName;
        toRet.currentIsDesignedForPump = this.currentIsDesignedForPump;
        toRet.newIsDesignedForPump = this.newIsDesignedForPump;
        toRet.currentFinalUnitLitres = this.currentFinalUnitLitres == null ? null : this.currentFinalUnitLitres;
        toRet.newFinalUnitLitres = this.newFinalUnitLitres == null ? null : this.newFinalUnitLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "DutyUpdateLine.TenantId cannot be empty"},
            {field: "lineId", rule: (v: any) => !v.isEmpty() || "DutyUpdateLine.LineId cannot be empty"},
            {field: "currentManufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "DutyUpdateLine.CurrentManufacturerId cannot be empty"},
            {field: "newManufacturerId", rule: (v: any) => v == null || !v.isEmpty() || "DutyUpdateLine.NewManufacturerId cannot be empty"},
    ];
}


export class EprUpdateLine extends ModelObject<EprUpdateLine> {

    public tenantId: Guid = Guid.createEmpty();
    public isOrder: boolean = false;
    public lineId: Guid = Guid.createEmpty();
    public orderOrCreditNumber: string = "";
    public despatchDate: LocalDate = LocalDate.ofEpochDay(0);
    public productCode: string = "";
    public quantity: number = 0;
    private _currentEprBomId: Guid | null = null;
    public get currentEprBomId(): Guid | null { return this._currentEprBomId }
    public set currentEprBomId(value: Guid | null) {
      this._currentEprBomId = value === undefined ? null : value;
    }
    private _newEprBomId: Guid | null = null;
    public get newEprBomId(): Guid | null { return this._newEprBomId }
    public set newEprBomId(value: Guid | null) {
      this._newEprBomId = value === undefined ? null : value;
    }
    public currentEprBomName: string = "";
    public newEprBomName: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.tenantId = Guid.parseOrEmpty(jsonSerializedObject.tenantId);
          this.isOrder = jsonSerializedObject.isOrder;
          this.lineId = Guid.parseOrEmpty(jsonSerializedObject.lineId);
          this.orderOrCreditNumber = jsonSerializedObject.orderOrCreditNumber;
          this.despatchDate = LocalDate.parse(jsonSerializedObject.despatchDate);
          this.productCode = jsonSerializedObject.productCode;
          this.quantity = jsonSerializedObject.quantity;
          this.currentEprBomId = jsonSerializedObject.currentEprBomId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.currentEprBomId);
          this.newEprBomId = jsonSerializedObject.newEprBomId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.newEprBomId);
          this.currentEprBomName = jsonSerializedObject.currentEprBomName;
          this.newEprBomName = jsonSerializedObject.newEprBomName;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.tenantId = this.tenantId.toString();
        toRet.isOrder = this.isOrder;
        toRet.lineId = this.lineId.toString();
        toRet.orderOrCreditNumber = this.orderOrCreditNumber;
        toRet.despatchDate = this.despatchDate.toString();
        toRet.productCode = this.productCode;
        toRet.quantity = this.quantity;
        toRet.currentEprBomId = this.currentEprBomId == null ? null : this.currentEprBomId.toString();
        toRet.newEprBomId = this.newEprBomId == null ? null : this.newEprBomId.toString();
        toRet.currentEprBomName = this.currentEprBomName;
        toRet.newEprBomName = this.newEprBomName;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "tenantId", rule: (v: any) => !v.isEmpty() || "EprUpdateLine.TenantId cannot be empty"},
            {field: "lineId", rule: (v: any) => !v.isEmpty() || "EprUpdateLine.LineId cannot be empty"},
            {field: "currentEprBomId", rule: (v: any) => v == null || !v.isEmpty() || "EprUpdateLine.CurrentEprBomId cannot be empty"},
            {field: "newEprBomId", rule: (v: any) => v == null || !v.isEmpty() || "EprUpdateLine.NewEprBomId cannot be empty"},
    ];
}


export class SupportToolsService extends ServiceBase {

  public async getAllTenantSupportSummaries(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(TenantGroupSupportSummary)[]> {
    const req = {
    };
    const extractor = (response:any) => response.tenantGroups.map((v: any) => new TenantGroupSupportSummary(v));;
    return this.makeJsonRequest("supportTools/v1/getAllTenantSupportSummaries", req, false, extractor, opts);
  };

  public async getBillingSupportSummaries(tenantGroupIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(BillingSupportSummary)[]> {
    const req = {
    tenantGroupIds:tenantGroupIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.billingSummaries.map((v: any) => new BillingSupportSummary(v));;
    return this.makeJsonRequest("supportTools/v1/getBillingSupportSummaries", req, false, extractor, opts);
  };

  public async purgeAllPrefilledTenantGroups(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/purgeAllPrefilledTenantGroups", req, false, extractor, opts);
  };

  public async purgeTenantGroupById(tenantGroupId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/purgeTenantGroupById", req, false, extractor, opts);
  };

  public async populateMissingLatLongs(continuation: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContinuableLogString> {
    const req = {
    continuation:continuation,
    };
    const extractor = (response:any) => new ContinuableLogString(response.response);;
    return this.makeJsonRequest("supportTools/v1/populateMissingLatLongs", req, false, extractor, opts);
  };

  public async reevaluateAllOrderApproximateDutyAmounts(continuation: Guid | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ContinuableLog> {
    const req = {
    continuation:continuation == null ? null : continuation.toString(),
    };
    const extractor = (response:any) => new ContinuableLog(response.response);;
    return this.makeJsonRequest("supportTools/v1/reevaluateAllOrderApproximateDutyAmounts", req, false, extractor, opts);
  };

  public async sendUserFeedback(message: string, screenshot: Uint8Array | null, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    message:message,
    screenshot:converters.uint8ArrayToBase64(screenshot),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/sendUserFeedback", req, false, extractor, opts);
  };

  public async updateBillingFreeTrialEndInstant(tenantGroupId: Guid, newFreeTrialEndInstant: Instant, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    newFreeTrialEndInstant:newFreeTrialEndInstant.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/updateBillingFreeTrialEndInstant", req, false, extractor, opts);
  };

  public async billingValidateAndRepairCustomerSubscriptions(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(string)[]> {
    const req = {
    };
    const extractor = (response:any) => response.log.map((v: any) => v);;
    return this.makeJsonRequest("supportTools/v1/billingValidateAndRepairCustomerSubscriptions", req, false, extractor, opts);
  };

  public async setTenantLocation(tenantId: Guid, locationCountryCode: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    locationCountryCode:locationCountryCode,
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/setTenantLocation", req, false, extractor, opts);
  };

  public async setTenantGroupName(tenantGroupId: Guid, tenantGroupName: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    tenantGroupName:tenantGroupName,
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/setTenantGroupName", req, false, extractor, opts);
  };

  public async generateEmailVerificationLinkForUser(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.link;;
    return this.makeJsonRequest("supportTools/v1/generateEmailVerificationLinkForUser", req, false, extractor, opts);
  };

  public async generatePasswordResetLinkForUser(tenantGroupId: Guid, userId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    tenantGroupId:tenantGroupId.toString(),
    userId:userId.toString(),
    };
    const extractor = (response:any) => response.link;;
    return this.makeJsonRequest("supportTools/v1/generatePasswordResetLinkForUser", req, false, extractor, opts);
  };

  public async migrateAssembliesToMegaBatches(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/migrateAssembliesToMegaBatches", req, false, extractor, opts);
  };

  public async getDutyUpdateOrderLines(tenantId: Guid, fromDate: LocalDate, toDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutyUpdateLine)[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    };
    const extractor = (response:any) => response.updateLines.map((v: any) => new DutyUpdateLine(v));;
    return this.makeJsonRequest("supportTools/v1/getDutyUpdateOrderLines", req, false, extractor, opts);
  };

  public async generateDutyUpdateLinesCsv(updateLines: readonly DutyUpdateLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    updateLines:updateLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.csv;;
    return this.makeJsonRequest("supportTools/v1/generateDutyUpdateLinesCsv", req, false, extractor, opts);
  };

  public async updateOrderDutyLines(tenantId: Guid, updateLines: readonly DutyUpdateLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    updateLines:updateLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/updateOrderDutyLines", req, false, extractor, opts);
  };

  public async getDutyUpdateCreditLines(tenantId: Guid, fromDate: LocalDate, toDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(DutyUpdateLine)[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    };
    const extractor = (response:any) => response.updateLines.map((v: any) => new DutyUpdateLine(v));;
    return this.makeJsonRequest("supportTools/v1/getDutyUpdateCreditLines", req, false, extractor, opts);
  };

  public async updateCreditDutyLines(tenantId: Guid, updateLines: readonly DutyUpdateLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    updateLines:updateLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/updateCreditDutyLines", req, false, extractor, opts);
  };

  public async getEprUpdateLines(tenantId: Guid, fromDate: LocalDate, toDate: LocalDate, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(EprUpdateLine)[]> {
    const req = {
    tenantId:tenantId.toString(),
    fromDate:fromDate.toString(),
    toDate:toDate.toString(),
    };
    const extractor = (response:any) => response.updateLines.map((v: any) => new EprUpdateLine(v));;
    return this.makeJsonRequest("supportTools/v1/getEprUpdateLines", req, false, extractor, opts);
  };

  public async generateEprUpdateLinesCsv(updateLines: readonly EprUpdateLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<string> {
    const req = {
    updateLines:updateLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.csv;;
    return this.makeJsonRequest("supportTools/v1/generateEprUpdateLinesCsv", req, false, extractor, opts);
  };

  public async updateOrderCreditLinesEpr(tenantId: Guid, updateLines: readonly EprUpdateLine[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    updateLines:updateLines.map(v => v.toJsonSerializedObject()),
    };
    const extractor = null;
    return this.makeJsonRequest("supportTools/v1/updateOrderCreditLinesEpr", req, false, extractor, opts);
  };
}

export const supportToolsService = new SupportToolsService();